import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Sparkles, 
  UtensilsCrossed, 
  Gem, 
  Coins, 
  Plane, 
  Building, 
  Clock, 
  ExternalLink, 
  MapPin, 
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  Award,
  Compass,
  Landmark,
  Moon,
  HeartHandshake,
  Wine
} from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useLang } from '@/contexts/LanguageContext';

const COMMON_IATA = {
  // Italia
  'venezia': 'VCE', 'venice': 'VCE', 'marghera': 'VCE', 'mestre': 'VCE', 'treviso': 'TSF',
  'bari': 'BRI', 'roma': 'ROM', 'rome': 'ROM', 'fiumicino': 'FCO', 'ciampino': 'CIA',
  'milano': 'MIL', 'milan': 'MIL', 'malpensa': 'MXP', 'linate': 'LIN', 'bergamo': 'BGY',
  'napoli': 'NAP', 'naples': 'NAP', 'firenze': 'FLR', 'florence': 'FLR', 'pisa': 'PSA',
  'bologna': 'BLQ', 'palermo': 'PMO', 'catania': 'CTA', 'torino': 'TRN', 'turin': 'TRN',
  'verona': 'VRN', 'genova': 'GOA', 'genoa': 'GOA', 'cagliari': 'CAG', 'olbia': 'OLB',
  'alghero': 'AHO', 'brindisi': 'BDS', 'lamezia': 'SUF', 'trieste': 'TRS', 'reggio calabria': 'REG',
  
  // Balcani & Est Europa
  'tirana': 'TIA', 'belgrado': 'BEG', 'sarajevo': 'SJJ', 'skopje': 'SKP', 'podgorica': 'TGD',

  // Capitali & Mete Europee
  'vienna': 'VIE', 'parigi': 'PAR', 'paris': 'PAR', 'londra': 'LON', 'london': 'LON',
  'madrid': 'MAD', 'barcellona': 'BCN', 'barcelona': 'BCN', 'lisbona': 'LIS', 'lisbon': 'LIS',
  'berlino': 'BER', 'berlin': 'BER', 'monaco': 'MUC', 'munich': 'MUC', 'amsterdam': 'AMS',
  'bruxelles': 'BRU', 'brussels': 'BRU', 'praga': 'PRG', 'prague': 'PRG', 'budapest': 'BUD',
  'atene': 'ATH', 'dublino': 'DUB', 'stoccolma': 'ARN', 'copenaghen': 'CPH', 'oslo': 'OSL',
  'helsinki': 'HEL', 'varsavia': 'WAW', 'cracovia': 'KRK', 'bucarest': 'OTP', 'zurigo': 'ZRH',
  'siviglia': 'SVQ', 'valencia': 'VLC', 'porto': 'OPO', 'malta': 'MLA', 'santorini': 'JTR',
  'ibiza': 'IBZ', 'maiorca': 'PMI', 'palma': 'PMI', 'cannes': 'NCE', 'nizza': 'NCE', 'nice': 'NCE'
};

function formatDateToDDMM(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}${parts[1]}`;
  }
  return '';
}

function getInstantIata(cityName) {
  if (!cityName) return '';
  const clean = cityName.trim().toLowerCase();
  if (clean.length === 3 && /^[a-z]{3}$/i.test(clean)) {
    return clean.toUpperCase();
  }
  return COMMON_IATA[clean] || '';
}

export default function PlanTrip() {
  const { lang } = useLang();
  const isEn = lang === 'en';
  const [searchParams] = useSearchParams();
  const initialDestination = searchParams.get('destination') || '';

  const [destination, setDestination] = useState(initialDestination);
  const [origin, setOrigin] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [people, setPeople] = useState(2);
  const [budgetLevel, setBudgetLevel] = useState('medio');
  const [tripStyle, setTripStyle] = useState('mix');

  const [flightArrivalTime, setFlightArrivalTime] = useState('11:30');
  const [flightDepartureTime, setFlightDepartureTime] = useState('18:30');
  const [accommodationName, setAccommodationName] = useState('');

  const [originIata, setOriginIata] = useState('');
  const [destIata, setDestIata] = useState(getInstantIata(initialDestination));

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(10);
  const navigate = useNavigate();

  const isItalianSite = !isEn;
  const currentCurrency = isItalianSite ? 'EUR' : 'USD';
  const currentLocale = isItalianSite ? 'it' : 'en';
  const aviasalesDomain = isItalianSite ? 'www.aviasales.it' : 'www.aviasales.com';
  const marker = "752211";

  const fetchIata = async (cityName) => {
    if (!cityName) return '';
    const instant = getInstantIata(cityName);
    if (instant) return instant;

    try {
      const res = await fetch(`https://autocomplete.travelpayouts.com/places2?locale=${currentLocale}&types[]=city&types[]=airport&term=${encodeURIComponent(cityName.trim())}`);
      const data = await res.json();
      if (data && data.length > 0) {
        return data[0].code || data[0].city_code || '';
      }
    } catch (e) {
      console.warn("Risoluzione IATA non riuscita:", e);
    }
    return '';
  };

  useEffect(() => {
    if (!origin.trim()) {
      setOriginIata('');
      return;
    }
    const instant = getInstantIata(origin);
    if (instant) {
      setOriginIata(instant);
    } else {
      const timer = setTimeout(() => {
        fetchIata(origin).then(code => code && setOriginIata(code));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [origin]);

  useEffect(() => {
    if (!destination.trim()) {
      setDestIata('');
      return;
    }
    const instant = getInstantIata(destination);
    if (instant) {
      setDestIata(instant);
    } else {
      const timer = setTimeout(() => {
        fetchIata(destination).then(code => code && setDestIata(code));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [destination]);

  useEffect(() => {
    let timer;
    if (loading) {
      setProgress(10);
      timer = setInterval(() => {
        setProgress((old) => (old >= 90 ? 90 : Math.min(old + Math.random() * 15, 90)));
      }, 400);
    } else {
      setProgress(100);
    }
    return () => clearInterval(timer);
  }, [loading]);

  const effectiveOriginIata = originIata || getInstantIata(origin) || (origin.trim().length === 3 ? origin.trim().toUpperCase() : '');
  const effectiveDestIata = destIata || getInstantIata(destination) || (destination.trim().length === 3 ? destination.trim().toUpperCase() : '');

  const handleOpenFlight = (e) => {
    e.preventDefault();
    const orig = effectiveOriginIata;
    const dest = effectiveDestIata;
    const dep = formatDateToDDMM(departureDate);
    const ret = formatDateToDDMM(returnDate);
    const pax = Number(people) || 1;

    let targetUrl = `https://${aviasalesDomain}/?marker=${marker}&currency=${currentCurrency.toLowerCase()}&locale=${currentLocale}`;

    if (orig && dest && dep && ret) {
      targetUrl = `https://${aviasalesDomain}/search/${orig}${dep}${dest}${ret}${pax}?marker=${marker}&currency=${currentCurrency.toLowerCase()}&locale=${currentLocale}`;
    } else if (orig && dest && dep) {
      targetUrl = `https://${aviasalesDomain}/search/${orig}${dep}${dest}${pax}?marker=${marker}&currency=${currentCurrency.toLowerCase()}&locale=${currentLocale}`;
    } else if (orig && dest) {
      targetUrl = `https://${aviasalesDomain}/search/${orig}${dest}${pax}?marker=${marker}&currency=${currentCurrency.toLowerCase()}&locale=${currentLocale}`;
    }

    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenHotel = (e) => {
    e.preventDefault();
    const pax = Number(people) || 2;
    const hotelUrl = `https://search.hotellook.com/?destination=${encodeURIComponent(destination || (isEn ? 'Paris' : 'Barcellona'))}&checkIn=${departureDate || ''}&checkOut=${returnDate || ''}&adults=${pax}&marker=${marker}&currency=${currentCurrency.toLowerCase()}&language=${currentLocale}`;
    window.open(hotelUrl, '_blank', 'noopener,noreferrer');
  };

  const handleGenerateTrip = async (e) => {
    e.preventDefault();

    if (departureDate && returnDate && returnDate < departureDate) {
      alert(isEn ? "Return date cannot be earlier than departure date!" : "La data di rientro non può essere precedente alla data di partenza!");
      return;
    }

    setLoading(true);

    const estimatedBudgets = { economico: 500, medio: 1200, lusso: 3000 };

    try {
      const payload = {
        destination,
        origin,
        departure_date: departureDate,
        return_date: returnDate,
        people: Number(people),
        budget: estimatedBudgets[budgetLevel] || 1000,
        budget_level: budgetLevel,
        trip_style: tripStyle,
        flight_arrival_time: flightArrivalTime,
        flight_departure_time: flightDepartureTime,
        accommodation_name: accommodationName,
        language: isEn ? 'en' : 'it'
      };

      const res = await api.post('/trips/generate', payload);
      setProgress(100);
      navigate(`/trip/${res.data.id}`);
    } catch (err) {
      if (err?.response?.status === 401) {
        alert(isEn ? "Session expired. Please log in again." : "Sessione scaduta o non valida. Effettua nuovamente il login.");
        navigate('/login');
      } else {
        alert((isEn ? "Error: " : "Errore: ") + formatApiError(err));
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1437] text-white p-4 md:p-8 flex flex-col items-center relative">
      {/* Regola CSS per colorare le icone di calendario e orologio in oro/giallo (#D4AF37) */}
      <style dangerouslySetInnerHTML={{ __html: `
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: invert(73%) sepia(55%) saturate(548%) hue-rotate(5deg) brightness(92%) contrast(89%);
          opacity: 0.95;
          transition: transform 0.15s ease, opacity 0.15s ease;
        }
        input[type="date"]::-webkit-calendar-picker-indicator:hover,
        input[type="time"]::-webkit-calendar-picker-indicator:hover {
          transform: scale(1.1);
          opacity: 1;
        }
      `}} />

      {loading && (
        <div className="fixed inset-0 w-full h-full z-[999999] bg-[#0b1437] flex flex-col items-center justify-center p-6 text-center">
          <div className="relative mb-8">
            <div className="w-24 h-24 rounded-full border-4 border-[#D4AF37]/20 border-t-[#D4AF37] animate-spin flex items-center justify-center"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-[#D4AF37] animate-pulse" />
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-serif text-[#EDE7D8] mb-3 tracking-wide">
            {isEn ? "The Genie is crafting your itinerary..." : "Il Genio sta creando il tuo itinerario..."}
          </h2>
          <p className="text-[#B79A5A] max-w-lg text-sm md:text-base mb-8">
            {isEn 
              ? `Coordinating airport transfers (${flightArrivalTime} arrival - ${flightDepartureTime} departure) and handpicking top venues in ` 
              : `Coordinamento transfer aeroportuali (${flightArrivalTime} arrivo - ${flightDepartureTime} rientro) e selezione dei ristoranti più quotati per `}
            <span className="text-[#EDE7D8] font-semibold">{destination || (isEn ? "your destination" : "la tua meta")}</span>.
          </p>

          <div className="w-72 md:w-96 bg-[#1b2559] rounded-full h-3 p-0.5 border border-[#D4AF37]/30 shadow-inner overflow-hidden">
            <div 
              className="bg-gradient-to-r from-[#7A5A18] via-[#D4AF37] to-[#F1D26B] h-full rounded-full transition-all duration-300 ease-out shadow-[0_0_12px_rgba(212,175,55,0.6)]"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="text-xs text-gray-400 mt-3 font-mono">{Math.round(progress)}% {isEn ? "completed" : "completato"}</span>
        </div>
      )}

      <div className="max-w-3xl w-full bg-[#111c44] p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-800">
        
        {/* Intestazione */}
        <div className="text-center mb-6">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold flex items-center justify-center gap-1.5">
            <Award className="w-4 h-4 text-[#d4af37]" />
            <span>{isEn ? "Official Planner • Holiday Genie" : "Pianificatore Ufficiale • Il Genio in Vacanza"}</span>
          </span>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#ede7d8] mt-1">
            {isEn ? "Create Your Journey" : "Crea il tuo Viaggio"}
          </h1>
          <p className="text-xs md:text-sm text-gray-300 mt-1 max-w-xl mx-auto">
            {isEn 
              ? "Book flight and hotel through Genie's official channels for guaranteed best rates and an itinerary synchronized to your schedule." 
              : "Prenota volo e hotel attraverso i canali ufficiali del Genio per ottenere le migliori tariffe garantite e un itinerario perfettamente sincronizzato sui tuoi orari."}
          </p>
        </div>

        <form onSubmit={handleGenerateTrip} className="space-y-6">
          
          {/* FASE 1: DESTINAZIONE E DATE */}
          <div className="bg-[#152347] p-5 rounded-xl border border-gray-700/80 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#d4af37]">
              {isEn ? "1. Where & When Do You Want to Go?" : "1. Dove e Quando Vuoi Partire?"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  {isEn ? "Departure City" : "Città di Partenza"}
                </label>
                <input 
                  type="text" 
                  value={origin} 
                  onChange={(e) => setOrigin(e.target.value)} 
                  required 
                  placeholder={isEn ? "e.g. London, New York, Rome..." : "es. Venezia, Roma, Milano..."} 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  {isEn ? "Destination" : "Destinazione"}
                </label>
                <input 
                  type="text" 
                  value={destination} 
                  onChange={(e) => setDestination(e.target.value)} 
                  required 
                  placeholder={isEn ? "e.g. Paris, Tokyo, Barcelona, Prague..." : "es. Barcellona, Tirana, Lisbona, Parigi..."} 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  {isEn ? "Departure Date" : "Data Partenza"}
                </label>
                <input 
                  type="date" 
                  value={departureDate} 
                  onChange={(e) => setDepartureDate(e.target.value)} 
                  required 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  {isEn ? "Return Date" : "Data Rientro"}
                </label>
                <input 
                  type="date" 
                  value={returnDate} 
                  min={departureDate}
                  onChange={(e) => setReturnDate(e.target.value)} 
                  required 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  {isEn ? "Travelers" : "Passeggeri"}
                </label>
                <input 
                  type="number" 
                  min="1" 
                  max="20" 
                  value={people} 
                  onChange={(e) => setPeople(e.target.value)} 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
              </div>
            </div>
          </div>

          {/* FASE 2: PRENOTAZIONE VOLO E HOTEL */}
          <div className="bg-[#12204f] border-2 border-[#d4af37] rounded-xl p-5 shadow-2xl relative">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#d4af37]" />
                <h4 className="text-sm md:text-base font-bold text-white">
                  {isEn ? "2. Book Flight and Hotel with Genie's Partners" : "2. Prenota Volo e Hotel con i Canali del Genio"}
                </h4>
              </div>
              <span className="text-xs bg-[#0b1437] px-2.5 py-1 rounded-lg border border-gray-700 text-[#d4af37] font-mono">
                {isEn ? "Route: " : "Tratta: "}{effectiveOriginIata || '???'} &rarr; {effectiveDestIata || '???'}
              </span>
            </div>

            <p className="text-xs text-gray-200 mb-4 leading-relaxed">
              {isEn 
                ? "Genie Tip: Always book flight and accommodation via the buttons below to lock in the best rate and sync your transfers seamlessly:" 
                : "Consiglio del Genio: Prenota sempre il volo e l'alloggio tramite i nostri pulsanti ufficiali qui sotto. Oltre ad accedere al comparatore più vantaggioso a costo zero, sostieni il Genio e sblocchi la sincronizzazione automatica dei transfer:"}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <button
                type="button"
                onClick={handleOpenFlight}
                className="flex items-center justify-center gap-2 p-3.5 bg-[#d4af37] hover:bg-[#b8962d] text-[#0b1437] font-bold rounded-xl text-xs transition shadow-lg cursor-pointer"
              >
                <Plane className="w-4 h-4" />
                <span>{isEn ? "Find & Lock Flight (Aviasales)" : "Trova & Blocca il Volo (Aviasales)"}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={handleOpenHotel}
                className="flex items-center justify-center gap-2 p-3.5 bg-[#1b2559] hover:bg-[#23306f] text-white border border-[#d4af37]/50 font-semibold rounded-xl text-xs transition shadow-lg cursor-pointer"
              >
                <Building className="w-4 h-4 text-[#d4af37]" />
                <span>{isEn ? "Book Partner Hotel (Hotellook)" : "Prenota Hotel Partner (Hotellook)"}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-[11px] text-gray-300 pt-1 border-t border-gray-700/60">
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" /> {isEn ? "Currency: " : "Valuta: "}{currentCurrency}
              </span>
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" /> {isEn ? "No extra fees" : "Nessun costo aggiuntivo"}
              </span>
              <span className="flex items-center gap-1 text-amber-300">
                <CheckCircle className="w-3.5 h-3.5" /> {isEn ? "Compare 700+ airlines" : "Oltre 700 compagnie"}
              </span>
            </div>
          </div>

          {/* FASE 3: ORARI VOLO */}
          <div className="bg-[#152347] p-5 rounded-xl border border-gray-700/80 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#d4af37]">
              {isEn ? "3. Enter Selected Flight Times" : "3. Inserisci gli Orari del Volo Scelto"}
            </h3>
            <p className="text-xs text-gray-300">
              {isEn 
                ? "After selecting your flight, enter arrival and departure times so the Genie calculates airport buffers and transfers." 
                : "Dopo aver individuato il volo su Aviasales, inserisci gli orari: il Genio calcolerà i tempi di sbarco, il transfer in hotel e l'arrivo anticipato in aeroporto al ritorno."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-[#d4af37]" />
                  <span>{isEn ? "Arrival time (Outbound)" : "Orario atterraggio andata"}</span>
                </label>
                <input 
                  type="time" 
                  value={flightArrivalTime} 
                  onChange={(e) => setFlightArrivalTime(e.target.value)} 
                  required 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
                <span className="text-[10px] text-gray-400 mt-1 block">
                  {isEn ? "Activities start right after transfer and check-in." : "Le visite del primo giorno partiranno subito dopo il transfer e il check-in."}
                </span>
              </div>

              <div>
                <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-[#d4af37]" />
                  <span>{isEn ? "Departure time (Return)" : "Orario decollo rientro"}</span>
                </label>
                <input 
                  type="time" 
                  value={flightDepartureTime} 
                  onChange={(e) => setFlightDepartureTime(e.target.value)} 
                  required 
                  className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
                />
                <span className="text-[10px] text-gray-400 mt-1 block">
                  {isEn ? "Transfer to airport will be planned ~2h30m prior to takeoff." : "L'itinerario calcolerà il transfer verso l'aeroporto circa 2h30m prima del decollo."}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-[#d4af37]" />
                <span>{isEn ? "Hotel name or neighborhood (Optional)" : "Nome hotel o zona di alloggio (Opzionale)"}</span>
              </label>
              <input 
                type="text" 
                value={accommodationName} 
                onChange={(e) => setAccommodationName(e.target.value)} 
                placeholder={isEn ? "e.g. City Center, Marriott, Gothic Quarter..." : "es. Barcellona Centro, Hotel Gotico, Tirana City Center..."} 
                className="w-full p-2.5 rounded-lg bg-[#0b1437] border border-gray-700 text-xs focus:outline-none focus:border-[#d4af37]"
              />
            </div>
          </div>

          {/* FASE 4: STILE DEL VIAGGIO (5 CATEGORIE) */}
          <div className="bg-[#152347] p-5 rounded-xl border border-gray-700/80 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#d4af37]">
              {isEn ? "4. What Type of Experience Do You Prefer?" : "4. Che Tipo di Vacanza Preferisci?"}
            </h3>
            <p className="text-xs text-gray-300">
              {isEn
                ? "The Genie adapts daytime rhythms, waking hours, and highlights based on your lifestyle."
                : "Il Genio calibrerà gli orari della giornata, le ore di sonno e le tappe in base al tuo stile di viaggio."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <button
                type="button"
                onClick={() => setTripStyle('mix')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  tripStyle === 'mix'
                    ? 'border-[#d4af37] bg-[#d4af37]/15 text-white ring-1 ring-[#d4af37]'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400 hover:border-gray-500'
                }`}
              >
                <Compass className="w-5 h-5 mb-1.5 text-[#d4af37]" />
                <span className="font-bold text-xs text-white">{isEn ? "Mix All-in-One" : "Mix & Armonia"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "The best of all worlds" : "Il meglio di tutto ben bilanciato"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setTripStyle('cultura')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  tripStyle === 'cultura'
                    ? 'border-blue-400 bg-blue-950/50 text-blue-200 ring-1 ring-blue-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400 hover:border-gray-500'
                }`}
              >
                <Landmark className="w-5 h-5 mb-1.5 text-blue-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Art & Culture" : "Arte & Musei"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "History, sights & early start" : "Monumenti, storia e mattinate attive"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setTripStyle('movida')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  tripStyle === 'movida'
                    ? 'border-pink-500 bg-pink-950/50 text-pink-200 ring-1 ring-pink-500'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400 hover:border-gray-500'
                }`}
              >
                <Moon className="w-5 h-5 mb-1.5 text-pink-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Nightlife" : "Movida & Notte"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Bars, clubs & late mornings" : "Club, rooftop e risveglio tardivo"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setTripStyle('benessere')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  tripStyle === 'benessere'
                    ? 'border-emerald-400 bg-emerald-950/50 text-emerald-200 ring-1 ring-emerald-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400 hover:border-gray-500'
                }`}
              >
                <HeartHandshake className="w-5 h-5 mb-1.5 text-emerald-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Spa & Wellness" : "Benessere & Spa"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Thermal baths, slow pace" : "Terme, relax e ritmi distesi"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setTripStyle('gusto')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  tripStyle === 'gusto'
                    ? 'border-amber-400 bg-amber-950/50 text-amber-200 ring-1 ring-amber-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400 hover:border-gray-500'
                }`}
              >
                <Wine className="w-5 h-5 mb-1.5 text-amber-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Food & Wine" : "Gusto & Tradizione"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Markets & tastings" : "Mercati storici ed enogastronomia"}
                </span>
              </button>
            </div>
          </div>

          {/* FASE 5: FASCIA RISTORANTI */}
          <div className="bg-[#152347] p-5 rounded-xl border border-gray-700/80 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#d4af37]">
              {isEn ? "5. Dining & Restaurant Level" : "5. Fascia Ristoranti Consigliati"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setBudgetLevel('economico')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  budgetLevel === 'economico'
                    ? 'border-emerald-400 bg-emerald-950/50 text-emerald-200 ring-1 ring-emerald-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400'
                }`}
              >
                <Coins className="w-4 h-4 mb-1 text-emerald-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Budget" : "Economico"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Local taverns & street food" : "Trattorie tipiche e street food autentico"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setBudgetLevel('medio')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  budgetLevel === 'medio'
                    ? 'border-amber-400 bg-amber-950/50 text-amber-200 ring-1 ring-amber-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400'
                }`}
              >
                <UtensilsCrossed className="w-4 h-4 mb-1 text-amber-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Balanced" : "Medio"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Authentic quality bistros" : "Ristoranti tradizionali e alta qualità/prezzo"}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setBudgetLevel('lusso')}
                className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition ${
                  budgetLevel === 'lusso'
                    ? 'border-purple-400 bg-purple-950/50 text-purple-200 ring-1 ring-purple-400'
                    : 'border-gray-700 bg-[#0b1437] text-gray-400'
                }`}
              >
                <Gem className="w-4 h-4 mb-1 text-purple-400" />
                <span className="font-bold text-xs text-white">{isEn ? "Luxury" : "Lusso"}</span>
                <span className="text-[10px] text-gray-300 mt-1">
                  {isEn ? "Fine dining & Michelin stars" : "Fine dining panoramico o stellato"}
                </span>
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-[#d4af37] text-[#0b1437] font-bold p-4 rounded-xl hover:bg-[#b8962d] transition duration-200 disabled:opacity-50 shadow-2xl text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
          >
            <Sparkles className="w-4 h-4" />
            <span>{loading ? (isEn ? "The Genie is weaving the journey..." : "Il Genio sta creando il viaggio...") : (isEn ? "Generate Itinerary with Transfers" : "Genera Itinerario con Transfer")}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
